/**
 * Firma del cliente sobre lienzo táctil.
 *
 * Reemplaza a `react-native-signature-canvas` de la app móvil, sin dependencia:
 * son eventos de puntero sobre un `<canvas>`. Funciona con dedo, con lápiz y con
 * ratón, y se exporta en PNG con fondo blanco para que se vea igual impresa.
 */

import { useEffect, useRef, useState } from 'react'

export default function Firma({ firmaPrevia, nombreSugerido, onGuardar }) {
  const lienzo = useRef(null)
  const [vacio, setVacio] = useState(true)
  const [firmante, setFirmante] = useState(nombreSugerido || '')

  useEffect(() => {
    const cv = lienzo.current
    const dpr = window.devicePixelRatio || 1
    const caja = cv.getBoundingClientRect()
    cv.width = caja.width * dpr
    cv.height = caja.height * dpr
    const ctx = cv.getContext('2d')
    ctx.scale(dpr, dpr)
    ctx.lineWidth = 2.4
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = getComputedStyle(document.body).color

    let trazando = false
    const punto = ev => {
      const c = cv.getBoundingClientRect()
      const t = ev.touches ? ev.touches[0] : ev
      return [t.clientX - c.left, t.clientY - c.top]
    }
    const iniciar = ev => {
      ev.preventDefault()
      trazando = true
      setVacio(false)
      const [x, y] = punto(ev)
      ctx.beginPath()
      ctx.moveTo(x, y)
    }
    const mover = ev => {
      if (!trazando) return
      ev.preventDefault()
      const [x, y] = punto(ev)
      ctx.lineTo(x, y)
      ctx.stroke()
    }
    const soltar = () => { trazando = false }

    cv.addEventListener('mousedown', iniciar)
    cv.addEventListener('mousemove', mover)
    window.addEventListener('mouseup', soltar)
    cv.addEventListener('touchstart', iniciar, { passive: false })
    cv.addEventListener('touchmove', mover, { passive: false })
    cv.addEventListener('touchend', soltar)
    return () => {
      cv.removeEventListener('mousedown', iniciar)
      cv.removeEventListener('mousemove', mover)
      window.removeEventListener('mouseup', soltar)
      cv.removeEventListener('touchstart', iniciar)
      cv.removeEventListener('touchmove', mover)
      cv.removeEventListener('touchend', soltar)
    }
  }, [])

  const limpiar = () => {
    const cv = lienzo.current
    cv.getContext('2d').clearRect(0, 0, cv.width, cv.height)
    setVacio(true)
  }

  const guardar = () => {
    const cv = lienzo.current
    // Fondo blanco explícito: en tema oscuro el trazo es claro y se perdería al imprimir
    const salida = document.createElement('canvas')
    salida.width = cv.width
    salida.height = cv.height
    const s = salida.getContext('2d')
    s.fillStyle = '#fff'
    s.fillRect(0, 0, salida.width, salida.height)
    s.drawImage(cv, 0, 0)
    onGuardar(salida.toDataURL('image/png'), firmante.trim())
  }

  return (
    <>
      <div className="s" style={{ marginBottom: 10 }}>
        Firma con el dedo o el ratón. Queda guardada en la cotización y aparece impresa
        en la orden de servicio.
      </div>
      {firmaPrevia && (
        <div className="card">
          <div className="s" style={{ marginBottom: 6 }}>
            Ya hay una firma registrada. Si firmas de nuevo, la reemplazas.
          </div>
          <img src={firmaPrevia} alt="Firma registrada"
            style={{ width: '100%', background: '#fff', borderRadius: 2 }} />
        </div>
      )}
      <canvas ref={lienzo} className="lienzo" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
        <button className="ib" onClick={limpiar}>Limpiar</button>
        <button className="ib pri" disabled={vacio} onClick={guardar}>Guardar firma</button>
      </div>
      <label className="f" style={{ marginTop: 14 }}>
        <span>Nombre de quien firma</span>
        <input value={firmante} onChange={e => setFirmante(e.target.value)} />
      </label>
    </>
  )
}
